import React, { useState, useEffect, useMemo, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { useStore } from "../../../stores/store";
import { useSetMenu } from "../../../hooks/useSetMenu";
import FormLayoutSelection from "../../Generic/FormLayoutSelection";
import FormSelectSelection from "../../Generic/FormSelectSelection";
import CriterionValueGroup from "../../Generic/CriterionValueGroup";
import ToolPassportModal from "../../Generic/ToolPassportModal";
import Tooltip from "@mui/material/Tooltip";
import StarIcon from "@mui/icons-material/Star";
import TipsIcon from "@mui/icons-material/TipsAndUpdates";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import TuneIcon from "@mui/icons-material/Tune";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LinearProgress from "@mui/material/LinearProgress";

const Selection = () => {
  const { id, catid, panelid } = useParams();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const { mainStore } = useStore();
  useSetMenu(mainStore.ToolSelectionMenuType, mainStore.ToolSelectionMenuNo);

  // Modal for viewing passport details
  const [openModal, setOpenModal] = useState(false);
  const [selectedToolPassport, setSelectedToolPassport] = useState(null);

  const handleOpenPassport = async (toolId) => {
    try {
      const res = await axiosPrivate.get(`/ToolPassports/${toolId}`);
      if (res.data) {
        setSelectedToolPassport(res.data);
        setOpenModal(true);
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Cannot load tool passport", { variant: "error" });
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedToolPassport(null);
  };

  // State for selections and options
  const [panelOptions, setPanelOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [toolsInSubcategory, setToolsInSubcategory] = useState([]);

  // AI suggestions state
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [showAISuggestions, setShowAISuggestions] = useState(false);

  // Criteria loading state
  const [loadingCriteria, setLoadingCriteria] = useState(false);

  // Criteria groups
  const [criteriaW1, setCriteriaW1] = useState([]);
  const [criteriaW05, setCriteriaW05] = useState([]);
  const [criteriaW033, setCriteriaW033] = useState([]);
  const [selectedCVIdsW1, setSelectedCVIdsW1] = useState([]);
  const [selectedCVIdsW05, setSelectedCVIdsW05] = useState([]);
  const [selectedCVIdsW033, setSelectedCVIdsW033] = useState([]);

  // Evaluation method and result
  const [method, setMethod] = useState("MAUT");
  const [rankedTools, setRankedTools] = useState([]);
  const [evaluated, setEvaluated] = useState(false);

  // Load panels once
  useEffect(() => {
    const loadPanels = async () => {
      try {
        const res = await axiosPrivate.get("/Panels/SelectOptions");
        setPanelOptions(res.data);
        if (panelid && catid && id) {
          const selectedPanel = res.data.find((c) => c.value == panelid);
          setSelectedPanel(selectedPanel);

          const catRes = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${panelid}`);
          setCategoryOptions(Array.isArray(catRes.data) ? catRes.data : []);
          const selectedCategory = Array.isArray(catRes.data) ? catRes.data.find((c) => c.value == catid) : null;
          setSelectedCategory(selectedCategory);

          const subRes = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${catid}`);
          setSubcategoryOptions(Array.isArray(subRes.data) ? subRes.data : []);

          const selectedSubcategory = Array.isArray(subRes.data) ? subRes.data.find((c) => c.value == id) : null;
          setSelectedSubcategory(selectedSubcategory);


          const toolsRes = await axiosPrivate.get(`/ToolPassports/SelectOptionsBySubcategory/${id}`);
          setToolsInSubcategory(Array.isArray(toolsRes.data) ? toolsRes.data : []);
          const cRes = await axiosPrivate.get(`/Criterias/WithValuesBySubcategory/${id}`);
          const allCriteria = Array.isArray(cRes.data) ? cRes.data : [];
          setCriteriaW1(allCriteria.filter((c) => c.weightCoefficient === 1.0));
          setCriteriaW05(allCriteria.filter((c) => c.weightCoefficient === 0.5));
          setCriteriaW033(allCriteria.filter((c) => c.weightCoefficient === 0.33));

          setSelectedCVIdsW1([]);
          setSelectedCVIdsW05([]);
          setSelectedCVIdsW033([]);

        }
      } catch (err) {
        enqueueSnackbar("Cannot load panels", { variant: "error" });
      }
    };
    loadPanels();
  }, [axiosPrivate, enqueueSnackbar]);

  const resetFiled = () => {
    setEvaluated(false);
    setRankedTools([]);

    setCriteriaW1([]);
    setCriteriaW05([]);
    setCriteriaW033([]);

    setSelectedCVIdsW1([]);
    setSelectedCVIdsW05([]);
    setSelectedCVIdsW033([]);
  }

  // Handlers for cascading dropdowns
  const handlePanelChange = async (option) => {
    setSelectedPanel(option);
    setSelectedCategory(null);
    setCategoryOptions([]);
    setSelectedSubcategory(null);
    setSubcategoryOptions([]);
    setToolsInSubcategory([]);

    resetFiled();

    if (!option) return;
    try {
      const catRes = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${option.value}`);
      setCategoryOptions(Array.isArray(catRes.data) ? catRes.data : []);
    } catch (error) {
      enqueueSnackbar("Cannot load categories", { variant: "error" });
    }
  };

  const handleCategoryChange = async (option) => {

    setSelectedCategory(option);
    setSelectedSubcategory(null);
    setSubcategoryOptions([]);
    setToolsInSubcategory([]);

    resetFiled();
    if (!option) return;
    try {
      const subRes = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${option.value}`);
      setSubcategoryOptions(Array.isArray(subRes.data) ? subRes.data : []);
    } catch (err) {
      enqueueSnackbar("Cannot load subcategories", { variant: "error" });
    }
  };

  const handleSubcategoryChange = async (option) => {
    setSelectedSubcategory(option);
    setToolsInSubcategory([]);

    resetFiled();

    if (!option) return;

    setLoadingCriteria(true);
    try {
      const toolsRes = await axiosPrivate.get(`/ToolPassports/SelectOptionsBySubcategory/${option.value}`);
      setToolsInSubcategory(Array.isArray(toolsRes.data) ? toolsRes.data : []);
      const cRes = await axiosPrivate.get(`/Criterias/WithValuesBySubcategory/${option.value}`);
      const allCriteria = Array.isArray(cRes.data) ? cRes.data : [];
      setCriteriaW1(allCriteria.filter((c) => c.weightCoefficient === 1.0));
      setCriteriaW05(allCriteria.filter((c) => c.weightCoefficient === 0.5));
      setCriteriaW033(allCriteria.filter((c) => c.weightCoefficient === 0.33));

      // Reset selected criteria values
      setSelectedCVIdsW1([]);
      setSelectedCVIdsW05([]);
      setSelectedCVIdsW033([]);
    } catch (err) {
      enqueueSnackbar("Cannot load tools or criteria", { variant: "error" });
    } finally {
      setLoadingCriteria(false);
    }
  };

  // Handle method change
  const handleMethodChange = (e) => setMethod(e.target.value);

  // Parse AI JSON response - handles both JSON and legacy markdown
  const parseAIResponse = useCallback((response) => {
    if (!response) return null;

    // If it's already an object, return it
    if (typeof response === 'object') return response;

    // Try to parse as JSON
    try {
      // Remove any markdown code blocks if present
      let cleanResponse = response.trim();
      if (cleanResponse.startsWith('```json')) {
        cleanResponse = cleanResponse.slice(7);
      }
      if (cleanResponse.startsWith('```')) {
        cleanResponse = cleanResponse.slice(3);
      }
      if (cleanResponse.endsWith('```')) {
        cleanResponse = cleanResponse.slice(0, -3);
      }
      cleanResponse = cleanResponse.trim();

      const parsed = JSON.parse(cleanResponse);
      return parsed;
    } catch (e) {
      // If JSON parsing fails, return legacy format indicator
      return { legacy: true, content: response };
    }
  }, []);

  // Memoize parsed AI data
  const parsedAI = useMemo(
    () => parseAIResponse(aiSuggestions),
    [aiSuggestions, parseAIResponse]
  );

  // Evaluate decision-making
  const handleEvaluate = async () => {
    if (!selectedSubcategory) {
      enqueueSnackbar("No subcategory selected", { variant: "error" });
      return;
    }
    // Combine selected criteria value IDs
    const chosenCVIds = [
      ...selectedCVIdsW1,
      ...selectedCVIdsW05,
      ...selectedCVIdsW033,
    ];

    // Build a map for criteria weights
    const cvMap = {};
    const fillMap = (criteriaList) => {
      criteriaList.forEach((crit) => {
        crit.values.forEach((cv) => {
          cvMap[cv.id] = {
            criterionId: crit.id,
            weight: crit.weightCoefficient,
          };
        });
      });
    };
    fillMap(criteriaW1);
    fillMap(criteriaW05);
    fillMap(criteriaW033);

    const selectedCriteriaArray = [];
    const weightsMap = {};

    chosenCVIds.forEach((cvIdStr) => {
      const cvId = parseInt(cvIdStr, 10);
      const info = cvMap[cvId];
      if (info) {
        selectedCriteriaArray.push({
          criteriaId: info.criterionId,
          value: cvId,
          weight: info.weight,
        });
        weightsMap[info.criterionId] = info.weight;
      }
    });

    const criteriaWeightsArray = Object.entries(weightsMap).map(
      ([critIdStr, w]) => ({
        criteriaId: parseInt(critIdStr, 10),
        weight: w,
      })
    );

    // Prepare tools list from toolsInSubcategory
    const finalTools = toolsInSubcategory.map((tool) => ({
      toolId: parseInt(tool.value, 10),
    }));

    const decisionRequest = {
      Method: method,
      SubcategoryId: parseInt(selectedSubcategory.value, 10),
      SelectedCriteria: selectedCriteriaArray,
      SelectedTools: finalTools,
      CriteriaWeights: criteriaWeightsArray,
    };

    try {
      const response = await axiosPrivate.post(
        "/DecisionMaking/evaluate",
        decisionRequest
      );
      if (response.data) {
        setRankedTools(response.data);
        setEvaluated(true);
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error evaluating decision-making", {
        variant: "error",
      });
    }
  };

  // Get AI suggestions for the current ranking (triggered by the AI Recommendation button)
  const getAISuggestionsAfterEvaluation = async (tools) => {
    setLoadingAI(true);
    try {
      const rankedToolsRequest = tools.map((tool, idx) => ({
        toolId: tool.toolId,
        rank: idx + 1,
        score: tool.score
      }));

      const response = await axiosPrivate.post("/DecisionMaking/ai-suggestions", {
        rankedTools: rankedToolsRequest
      });

      if (response.data && response.data.suggestions) {
        setAiSuggestions(response.data.suggestions);
        setShowAISuggestions(true);
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to get AI suggestions", { variant: "error" });
    } finally {
      setLoadingAI(false);
    }
  };

  // Restart selection (reset evaluation)
  const handleRestart = () => {
    setEvaluated(false);
    setRankedTools([]);
    setAiSuggestions(null);
    setShowAISuggestions(false);
  };

  return (
    <FormLayoutSelection title={t("Tool Selection")}>
      <div className="row" style={{ marginBottom: "1rem", display: ((panelid && catid && id) ? 'none' : '') }} >
        <FormSelectSelection
          label="Panel"
          options={panelOptions}
          value={selectedPanel}
          onChange={handlePanelChange}
          disabled={panelid && catid && id}

        />
        <FormSelectSelection
          label="Category"
          options={categoryOptions}
          value={selectedCategory}
          onChange={handleCategoryChange}
          disabled={panelid && catid && id}
        />
        <FormSelectSelection
          label="Subcategory"
          options={subcategoryOptions}
          value={selectedSubcategory}
          onChange={handleSubcategoryChange}
          disabled={panelid && catid && id}
        />
      </div>
      <div className="row" style={{ marginBottom: "1rem", display: (!(panelid && catid && id) ? 'none' : '') }} >
        <h4 className=" newPrimaryBlue  p-1">{selectedPanel?.label + " / " + selectedCategory?.label + " / " + selectedSubcategory?.label}</h4>
      </div>

      {/* Loading state for criteria */}
      {loadingCriteria ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          borderRadius: '8px',
          padding: '2rem',
          marginBottom: '1rem'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid rgba(17, 45, 78, 0.1)',
            borderTop: '4px solid #112d4e',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            marginTop: '1rem',
            color: '#112d4e',
            fontSize: '1.1em',
            fontWeight: '600'
          }}>
            {t("Loading criteria...")}
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      ) : (
        <>
          {/* Mandatory Criteria (weight = 1.00) */}
          <div style={{ marginBottom: "1.5rem" }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 16px',
              background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
              borderRadius: '8px 8px 0 0',
              marginBottom: '12px'
            }}>
              <PriorityHighIcon sx={{ color: '#fff', fontSize: 22 }} />
              <div>
                <h4 style={{ margin: 0, color: '#fff', fontSize: '1em', fontWeight: '600' }}>
                  {t("Mandatory Criteria")} (W = 1.00)
                </h4>
                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75em' }}>
                  {t("Highest priority criteria")}
                </span>
              </div>
              <span style={{
                marginLeft: 'auto',
                background: 'rgba(255,255,255,0.2)',
                color: '#fff',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '0.75em',
                fontWeight: '600'
              }}>
                {selectedCVIdsW1.length} / {criteriaW1.reduce((acc, c) => acc + c.values.length, 0)} {t("selected")}
              </span>
            </div>
            {criteriaW1.length === 0 ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#94a3b8',
                background: '#f8fafc',
                borderRadius: '0 0 8px 8px',
                border: '1px dashed #e2e8f0'
              }}>
                {t("No mandatory criteria available")}
              </div>
            ) : (
              criteriaW1.map((crit) => (
                <CriterionValueGroup
                  key={crit.id}
                  criterion={crit}
                  selectedValueIds={selectedCVIdsW1}
                  setSelectedValueIds={setSelectedCVIdsW1}
                />
              ))
            )}
          </div>

          {/* Criteria (weight = 0.50) */}
          <div style={{ marginBottom: "1.5rem" }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 16px',
              background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
              borderRadius: '8px 8px 0 0',
              marginBottom: '12px'
            }}>
              <TuneIcon sx={{ color: '#fff', fontSize: 22 }} />
              <div>
                <h4 style={{ margin: 0, color: '#fff', fontSize: '1em', fontWeight: '600' }}>
                  {t("Criteria")} (W = 0.50)
                </h4>
                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75em' }}>
                  {t("Medium priority criteria")}
                </span>
              </div>
              <span style={{
                marginLeft: 'auto',
                background: 'rgba(255,255,255,0.2)',
                color: '#fff',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '0.75em',
                fontWeight: '600'
              }}>
                {selectedCVIdsW05.length} / {criteriaW05.reduce((acc, c) => acc + c.values.length, 0)} {t("selected")}
              </span>
            </div>
            {criteriaW05.length === 0 ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#94a3b8',
                background: '#f8fafc',
                borderRadius: '0 0 8px 8px',
                border: '1px dashed #e2e8f0'
              }}>
                {t("No criteria with weight 0.50 available")}
              </div>
            ) : (
              criteriaW05.map((crit) => (
                <CriterionValueGroup
                  key={crit.id}
                  criterion={crit}
                  selectedValueIds={selectedCVIdsW05}
                  setSelectedValueIds={setSelectedCVIdsW05}
                />
              ))
            )}
          </div>

          {/* Criteria (weight = 0.33) */}
          <div style={{ marginBottom: "1.5rem" }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 16px',
              background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              borderRadius: '8px 8px 0 0',
              marginBottom: '12px'
            }}>
              <AddCircleOutlineIcon sx={{ color: '#fff', fontSize: 22 }} />
              <div>
                <h4 style={{ margin: 0, color: '#fff', fontSize: '1em', fontWeight: '600' }}>
                  {t("Criteria")} (W = 0.33)
                </h4>
                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75em' }}>
                  {t("Lower priority criteria")}
                </span>
              </div>
              <span style={{
                marginLeft: 'auto',
                background: 'rgba(255,255,255,0.2)',
                color: '#fff',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '0.75em',
                fontWeight: '600'
              }}>
                {selectedCVIdsW033.length} / {criteriaW033.reduce((acc, c) => acc + c.values.length, 0)} {t("selected")}
              </span>
            </div>
            {criteriaW033.length === 0 ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#94a3b8',
                background: '#f8fafc',
                borderRadius: '0 0 8px 8px',
                border: '1px dashed #e2e8f0'
              }}>
                {t("No criteria with weight 0.33 available")}
              </div>
            ) : (
              criteriaW033.map((crit) => (
                <CriterionValueGroup
                  key={crit.id}
                  criterion={crit}
                  selectedValueIds={selectedCVIdsW033}
                  setSelectedValueIds={setSelectedCVIdsW033}
                />
              ))
            )}
          </div>
        </>
      )}

      {/* Selection Summary & Evaluation */}
      <div style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '1.5rem',
        border: '1px solid #e2e8f0'
      }}>
        {/* Selection Summary */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            padding: '12px',
            border: '1px solid #fecaca',
            borderLeft: '4px solid #ef4444'
          }}>
            <div style={{ fontSize: '0.75em', color: '#dc2626', fontWeight: '600', textTransform: 'uppercase' }}>
              W = 1.00
            </div>
            <div style={{ fontSize: '1.5em', fontWeight: '700', color: '#1e293b' }}>
              {selectedCVIdsW1.length}
            </div>
            <div style={{ fontSize: '0.7em', color: '#64748b' }}>
              {t("of")} {criteriaW1.reduce((acc, c) => acc + c.values.length, 0)} {t("options")}
            </div>
          </div>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            padding: '12px',
            border: '1px solid #fed7aa',
            borderLeft: '4px solid #f59e0b'
          }}>
            <div style={{ fontSize: '0.75em', color: '#d97706', fontWeight: '600', textTransform: 'uppercase' }}>
              W = 0.50
            </div>
            <div style={{ fontSize: '1.5em', fontWeight: '700', color: '#1e293b' }}>
              {selectedCVIdsW05.length}
            </div>
            <div style={{ fontSize: '0.7em', color: '#64748b' }}>
              {t("of")} {criteriaW05.reduce((acc, c) => acc + c.values.length, 0)} {t("options")}
            </div>
          </div>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            padding: '12px',
            border: '1px solid #bbf7d0',
            borderLeft: '4px solid #10b981'
          }}>
            <div style={{ fontSize: '0.75em', color: '#059669', fontWeight: '600', textTransform: 'uppercase' }}>
              W = 0.33
            </div>
            <div style={{ fontSize: '1.5em', fontWeight: '700', color: '#1e293b' }}>
              {selectedCVIdsW033.length}
            </div>
            <div style={{ fontSize: '0.7em', color: '#64748b' }}>
              {t("of")} {criteriaW033.reduce((acc, c) => acc + c.values.length, 0)} {t("options")}
            </div>
          </div>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            padding: '12px',
            border: '1px solid #c7d2fe',
            borderLeft: '4px solid #6366f1'
          }}>
            <div style={{ fontSize: '0.75em', color: '#4f46e5', fontWeight: '600', textTransform: 'uppercase' }}>
              {t("Tools")}
            </div>
            <div style={{ fontSize: '1.5em', fontWeight: '700', color: '#1e293b' }}>
              {toolsInSubcategory.length}
            </div>
            <div style={{ fontSize: '0.7em', color: '#64748b' }}>
              {t("to evaluate")}
            </div>
          </div>
        </div>

        {/* Evaluation Method Selection */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: '8px'
            }}>
              <label style={{ fontSize: '0.9em', fontWeight: '600', color: '#374151', margin: 0 }}>
                {t("Evaluation Method")}
              </label>
              <Tooltip
                title={
                  <div style={{ padding: '8px', maxWidth: '350px' }}>
                    <strong>MAUT</strong>: Multi-Attribute Utility Theory - Simple weighted scoring, best for straightforward comparisons.<br/><br/>
                    <strong>PROMETHEE</strong>: Outranking Method - Pairwise comparisons with net flow ranking.<br/><br/>
                    <strong>TOPSIS</strong>: Distance-based method - Ranks by closeness to ideal solution.<br/><br/>
                    <strong>VIKOR</strong>: Compromise ranking - Balances group utility and individual regret.<br/><br/>
                    <strong>COPRAS</strong>: Proportional assessment - Utility degree as percentage of best.
                  </div>
                }
                arrow
                placement="top"
              >
                <InfoOutlinedIcon sx={{ fontSize: 16, color: '#94a3b8', cursor: 'help' }} />
              </Tooltip>
            </div>
            <select
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: '0.95em',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                background: '#fff',
                color: '#1e293b',
                cursor: 'pointer',
                outline: 'none'
              }}
              value={method}
              onChange={handleMethodChange}
            >
              <option value="MAUT">MAUT - Multi-Attribute Utility Theory</option>
              <option value="PROMETHEE">PROMETHEE - Outranking Method</option>
              <option value="TOPSIS">TOPSIS - Distance to Ideal Solution</option>
              <option value="VIKOR">VIKOR - Compromise Ranking</option>
              <option value="COPRAS">COPRAS - Complex Proportional Assessment</option>
            </select>
          </div>
          <button
            style={{
              padding: '10px 28px',
              fontSize: '0.95em',
              fontWeight: '600',
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)'
            }}
            onClick={handleEvaluate}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.3)';
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 18 }} />
            {t("Evaluate Tools")}
          </button>
        </div>
      </div>

      {/* Evaluation Result */}
      {evaluated && (
        <div className="row" style={{ marginBottom: "1rem" }}>
          {/* Left column - Evaluation Results (40%) */}
          <div className="col-12 col-md-5">
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px'
            }}>
              <EmojiEventsIcon sx={{ color: '#f59e0b', fontSize: 24 }} />
              <h4 style={{ margin: 0, color: '#1e293b', fontSize: '1.1em', fontWeight: '600' }}>
                {t("Evaluation Results")}
              </h4>
            </div>
            {rankedTools.length === 0 ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#94a3b8',
                background: '#f8fafc',
                borderRadius: '8px',
                border: '1px dashed #e2e8f0'
              }}>
                {t("No results")}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {rankedTools.map((r, idx) => {
                  const maxScore = rankedTools[0]?.score || 1;
                  const scorePercentage = (r.score / maxScore) * 100;
                  const rankColors = {
                    0: { bg: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', border: '#f59e0b', badge: '#b45309', icon: '🥇' },
                    1: { bg: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)', border: '#94a3b8', badge: '#475569', icon: '🥈' },
                    2: { bg: 'linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)', border: '#ea580c', badge: '#9a3412', icon: '🥉' }
                  };
                  const colors = rankColors[idx] || { bg: '#fff', border: '#e2e8f0', badge: '#64748b', icon: `#${idx + 1}` };

                  return (
                    <div
                      key={r.toolId}
                      style={{
                        background: colors.bg,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '10px',
                        padding: '14px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <span style={{
                          fontSize: idx < 3 ? '1.3em' : '0.85em',
                          background: idx >= 3 ? colors.border : 'transparent',
                          color: idx >= 3 ? '#fff' : 'inherit',
                          width: idx >= 3 ? '24px' : 'auto',
                          height: idx >= 3 ? '24px' : 'auto',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '700'
                        }}>
                          {colors.icon}
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontWeight: '600',
                            fontSize: '0.95em',
                            color: '#1e293b'
                          }}>
                            {r.toolName}
                          </div>
                        </div>
                        <span style={{
                          background: colors.badge,
                          color: '#fff',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '0.75em',
                          fontWeight: '700'
                        }}>
                          {r.score.toFixed(3)}
                        </span>
                      </div>
                      {/* Score bar */}
                      <div style={{
                        height: '6px',
                        background: 'rgba(0,0,0,0.1)',
                        borderRadius: '3px',
                        overflow: 'hidden',
                        marginBottom: '10px'
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${scorePercentage}%`,
                          background: colors.badge,
                          borderRadius: '3px',
                          transition: 'width 0.5s ease'
                        }} />
                      </div>
                      <button
                        style={{
                          padding: '6px 14px',
                          fontSize: '0.8em',
                          fontWeight: '500',
                          background: '#fff',
                          color: '#2563eb',
                          border: '1px solid #2563eb',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                        onClick={() => handleOpenPassport(r.toolId)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#2563eb';
                          e.currentTarget.style.color = '#fff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#fff';
                          e.currentTarget.style.color = '#2563eb';
                        }}
                      >
                        {t("View Tool Passport")}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <button
                style={{
                  padding: '12px 28px',
                  fontSize: '1em',
                  fontWeight: '600',
                  background: '#112d4e',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: '100%',
                  maxWidth: '280px'
                }}
                onClick={handleRestart}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#1a4370';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#112d4e';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <i className="bi bi-arrow-repeat"></i>
                {t("Start New Evaluation")}
              </button>
            </div>
          </div>
          
          {/* Right column - AI Suggestions (60%) */}
          <div className="col-12 col-md-7">
            {!loadingAI && !showAISuggestions && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '200px',
                background: '#f8fafc',
                borderRadius: '8px',
                padding: '1.5rem',
                border: '1px solid #e2e8f0'
              }}>
                <AutoAwesomeIcon sx={{ fontSize: 32, color: '#3b82f6', mb: 1 }} />
                <p style={{ color: '#1e293b', fontSize: '0.9em', fontWeight: '600', margin: '0 0 4px 0' }}>
                  {t("AI Workflow Assistant")}
                </p>
                <p style={{ color: '#64748b', fontSize: '0.85em', margin: '0 0 14px 0', textAlign: 'center' }}>
                  {t("Generate an AI workflow recommendation based on this ranking.")}
                </p>
                <button
                  style={{
                    padding: '10px 24px',
                    fontSize: '0.95em',
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onClick={() => getAISuggestionsAfterEvaluation(rankedTools)}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                  {t("AI Recommendation")}
                </button>
              </div>
            )}
            {loadingAI ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '200px',
                background: '#f8fafc',
                borderRadius: '8px',
                padding: '1.5rem',
                border: '1px solid #e2e8f0'
              }}>
                <AutoAwesomeIcon sx={{ fontSize: 32, color: '#3b82f6', mb: 1 }} />
                <p style={{ color: '#1e293b', fontSize: '0.9em', fontWeight: '600', margin: 0 }}>
                  {t("AI Analysis in Progress")}
                </p>
                <LinearProgress sx={{ width: '40%', mt: 1.5, borderRadius: 1 }} />
              </div>
            ) : (
              showAISuggestions && parsedAI && (
                <div style={{
                  background: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden'
                }}>
                  {/* Header */}
                  <div style={{
                    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                    padding: '8px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <AutoAwesomeIcon sx={{ color: '#fbbf24', fontSize: 16 }} />
                    <span style={{ color: '#fff', fontWeight: '600', fontSize: '0.85em' }}>
                      AI Expert Analysis
                    </span>
                  </div>

                  {/* Content - flows naturally without scroll */}
                  <div style={{ padding: '12px' }}>
                    {parsedAI.legacy ? (
                      <div style={{ fontSize: '0.82em', lineHeight: '1.5', color: '#374151', whiteSpace: 'pre-wrap' }}>
                        {parsedAI.content}
                      </div>
                    ) : (
                      <>
                        {/* Section 1: Recommendation */}
                        {parsedAI.recommendation && (
                          <div style={{ marginBottom: '12px' }}>
                            {/* Tool Name & Verdict */}
                            <div style={{
                              background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                              borderRadius: '6px',
                              padding: '10px',
                              border: '1px solid #a7f3d0',
                              marginBottom: '8px'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                <StarIcon sx={{ color: '#059669', fontSize: 16 }} />
                                <span style={{ fontWeight: '700', color: '#065f46', fontSize: '0.9em' }}>
                                  {parsedAI.recommendation.toolName}
                                </span>
                              </div>
                              <p style={{ color: '#047857', fontSize: '0.8em', margin: 0, lineHeight: '1.4' }}>
                                {parsedAI.recommendation.verdict}
                              </p>
                              {parsedAI.recommendation.bestFor && (
                                <p style={{ color: '#065f46', fontSize: '0.75em', margin: '6px 0 0 0', fontStyle: 'italic' }}>
                                  Best for: {parsedAI.recommendation.bestFor}
                                </p>
                              )}
                            </div>

                            {/* Strengths & Limitations in 2 columns */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                              {/* Strengths */}
                              <div>
                                <div style={{ fontSize: '0.72em', fontWeight: '600', color: '#059669', marginBottom: '4px', textTransform: 'uppercase' }}>
                                  Strengths
                                </div>
                                {parsedAI.recommendation.strengths?.map((s, i) => (
                                  <div key={i} style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '4px',
                                    fontSize: '0.75em',
                                    color: '#374151',
                                    marginBottom: '3px',
                                    lineHeight: '1.3'
                                  }}>
                                    <span style={{ color: '#059669', flexShrink: 0 }}>✓</span>
                                    {s}
                                  </div>
                                ))}
                              </div>
                              {/* Limitations */}
                              {parsedAI.recommendation.limitations?.length > 0 && (
                                <div>
                                  <div style={{ fontSize: '0.72em', fontWeight: '600', color: '#d97706', marginBottom: '4px', textTransform: 'uppercase' }}>
                                    Limitations
                                  </div>
                                  {parsedAI.recommendation.limitations?.map((l, i) => (
                                    <div key={i} style={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: '4px',
                                      fontSize: '0.75em',
                                      color: '#374151',
                                      marginBottom: '3px',
                                      lineHeight: '1.3'
                                    }}>
                                      <WarningAmberIcon sx={{ color: '#d97706', fontSize: 12, flexShrink: 0, mt: '1px' }} />
                                      {l}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Section 2: Comparison */}
                        {parsedAI.comparison && (
                          <div style={{
                            background: '#f8fafc',
                            borderRadius: '6px',
                            padding: '10px',
                            marginBottom: '12px',
                            border: '1px solid #e2e8f0'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                              <CompareArrowsIcon sx={{ color: '#6366f1', fontSize: 14 }} />
                              <span style={{ fontSize: '0.75em', fontWeight: '600', color: '#4338ca', textTransform: 'uppercase' }}>
                                Tool Comparison
                              </span>
                            </div>
                            {parsedAI.comparison.summary && (
                              <p style={{ fontSize: '0.78em', color: '#475569', margin: '0 0 6px 0', lineHeight: '1.4' }}>
                                {parsedAI.comparison.summary}
                              </p>
                            )}
                            {parsedAI.comparison.toolNotes?.map((tn, i) => (
                              <div key={i} style={{
                                display: 'flex',
                                gap: '6px',
                                fontSize: '0.75em',
                                marginBottom: '2px'
                              }}>
                                <span style={{ color: '#6366f1', fontWeight: '600', flexShrink: 0 }}>{tn.tool}:</span>
                                <span style={{ color: '#64748b' }}>{tn.note}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Section 3: Workflow */}
                        {parsedAI.workflow && (
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{ fontSize: '0.75em', fontWeight: '600', color: '#0369a1', marginBottom: '6px', textTransform: 'uppercase' }}>
                              {parsedAI.workflow.title || 'Recommended Workflow'}
                            </div>
                            {parsedAI.workflow.description && (
                              <p style={{ fontSize: '0.78em', color: '#475569', margin: '0 0 8px 0', lineHeight: '1.4' }}>
                                {parsedAI.workflow.description}
                              </p>
                            )}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {parsedAI.workflow.steps?.map((step, i) => (
                                <div key={i} style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: '8px',
                                  padding: '6px 8px',
                                  background: '#f0f9ff',
                                  borderLeft: '2px solid #0ea5e9',
                                  borderRadius: '0 4px 4px 0'
                                }}>
                                  <span style={{
                                    background: '#0ea5e9',
                                    color: '#fff',
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.65em',
                                    fontWeight: '700',
                                    flexShrink: 0
                                  }}>{i + 1}</span>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginBottom: '2px' }}>
                                      <span style={{ fontSize: '0.72em', fontWeight: '600', color: '#0c4a6e' }}>
                                        {step.phase}
                                      </span>
                                      {step.tool && (
                                        <span style={{
                                          background: '#bae6fd',
                                          color: '#0369a1',
                                          padding: '1px 4px',
                                          borderRadius: '2px',
                                          fontSize: '0.65em',
                                          fontWeight: '500'
                                        }}>{step.tool}</span>
                                      )}
                                    </div>
                                    <div style={{ fontSize: '0.73em', color: '#64748b', lineHeight: '1.3' }}>
                                      {step.action}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Section 4: Tips */}
                        {parsedAI.tips?.length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>
                              <TipsIcon sx={{ color: '#d97706', fontSize: 14 }} />
                              <span style={{ fontSize: '0.75em', fontWeight: '600', color: '#b45309', textTransform: 'uppercase' }}>
                                Pro Tips
                              </span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                              {parsedAI.tips.map((tip, i) => (
                                <div key={i} style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: '4px',
                                  padding: '6px',
                                  background: '#fffbeb',
                                  borderRadius: '4px',
                                  fontSize: '0.73em',
                                  color: '#78350f',
                                  lineHeight: '1.3'
                                }}>
                                  <span style={{ color: '#f59e0b', flexShrink: 0 }}>•</span>
                                  {tip}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Section 5: Integration Info */}
                        {parsedAI.integration && (
                          <div style={{
                            background: '#faf5ff',
                            borderRadius: '6px',
                            padding: '8px 10px',
                            border: '1px solid #e9d5ff'
                          }}>
                            <div style={{ fontSize: '0.72em', fontWeight: '600', color: '#7c3aed', marginBottom: '6px', textTransform: 'uppercase' }}>
                              Integration & Compatibility
                            </div>
                            <div style={{ fontSize: '0.73em', color: '#5b21b6', lineHeight: '1.4' }}>
                              {parsedAI.integration.fileFormats && (
                                <div style={{ marginBottom: '4px' }}>
                                  <strong>Formats:</strong> {parsedAI.integration.fileFormats}
                                </div>
                              )}
                              {parsedAI.integration.compatibility && (
                                <div style={{ marginBottom: '4px' }}>
                                  <strong>Compatibility:</strong> {parsedAI.integration.compatibility}
                                </div>
                              )}
                              {parsedAI.integration.dataExchange && (
                                <div>
                                  <strong>Data Exchange:</strong> {parsedAI.integration.dataExchange}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* Modal to view a Tool Passport */}
      <ToolPassportModal
        open={openModal}
        onClose={handleCloseModal}
        toolPassport={selectedToolPassport}
      />
    </FormLayoutSelection>
  );
};

export default observer(Selection);